import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { ErrorLine } from '@uifabric/portal-ux/lib/ErrorLine';
import { LoadingLine } from '@uifabric/portal-ux/lib/LoadingLine';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useQos } from '@iamexperiences/ecos-telemetry';

import { AcceptPopup } from '../AcceptPopup/AcceptPopup';
import { DeclinePopup } from '../DeclinePopup/DeclinePopup';
import { TermsOfUseCard } from '../shared/TermsOfUseCard';
import { getRequestBody } from '../utilities/getRequestBody';
import { isTermsOfUseInlineAlertingEnabled } from '../utilities/isTermsOfUseInlineAlertingEnabled';
import { useGetAgreementUnauthenticated } from '../hooks/useGetAgreement';
import { useAcceptAgreement } from '../hooks/useAcceptAgreement';
import { useDeclineAgreement } from '../hooks/useDeclineAgreement';

import { classNames } from '../Consent/Consent.styles';

export interface IConsentPageContentProps {
  onTermsOfUseDecline: () => void;
  tenantDisplayName: string;
}

export const ConsentPageContent: React.FC<IConsentPageContentProps> = (props) => {
  const [t] = useLocalization('TermsOfUse');
  const { reportLoadComplete, reportFailure } = useQos('ConsentPageContent');

  const { requestBody, replyUri, agreementId } = getRequestBody();
  const getAgreementQuery = useGetAgreementUnauthenticated(agreementId);
  const agreement = getAgreementQuery.data;
  const policyFileId = agreement?.policyFiles.find((policyFile) => policyFile.isDefault)?.id;

  // Adding special inline UX for OOBE: "MSAppHost/<WebView Rev>" is added when EdgeHTML is hosted in a Universal Windows App using WebView.
  const isMSAppHost = navigator.userAgent.includes('MSAppHost/');
  const isInlineAlertingEnabled = isTermsOfUseInlineAlertingEnabled(isMSAppHost, replyUri);
  const declineInlineAlertingMessage = t('declineConfirmText', { orgName: props.tenantDisplayName });

  const acceptAgreementMutation = useAcceptAgreement();
  const declineAgreementMutation = useDeclineAgreement();
  const consentButtonsDisabled =
    acceptAgreementMutation.isLoading ||
    acceptAgreementMutation.isSuccess ||
    declineAgreementMutation.isLoading ||
    declineAgreementMutation.isSuccess;

  const [isDeclineConfirmationDialogHidden, setIsDeclineConfirmationDialogHidden] = useState(true);
  const [isViewingRequiredDialogHidden, setIsViewingRequiredDialogHidden] = useState(true);
  const [isTermsOfUseCardViewed, setIsTermsOfUseCardViewed] = useState(false);
  const [showDeclineInlineAlerting, setShowDeclineInlineAlerting] = useState(false);
  const [showAcceptInlineAlerting, setShowAcceptInlineAlerting] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const readWriteConsistencyTokenRef = useRef<HTMLInputElement>(null);
  const digitallySignedAcceptanceRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (getAgreementQuery.isError) {
      reportFailure(getAgreementQuery.error);
    } else if (!getAgreementQuery.isLoading) {
      reportLoadComplete();
    }
  }, [reportLoadComplete, reportFailure, getAgreementQuery]);

  useEffect(() => {
    const shouldShowInlineAlert =
      isInlineAlertingEnabled && agreement?.isViewingBeforeAcceptanceRequired && !isTermsOfUseCardViewed;

    if (shouldShowInlineAlert) {
      setShowAcceptInlineAlerting(true);
    } else {
      setShowAcceptInlineAlerting(false);
    }
  }, [agreement, isInlineAlertingEnabled, isTermsOfUseCardViewed]);

  if (getAgreementQuery.isLoading) {
    return <LoadingLine />;
  } else if (getAgreementQuery.isError || !agreement || !agreementId || !policyFileId) {
    return <ErrorLine>{t('messageTermsOfUseNotLoaded')}</ErrorLine>;
  } else {
    const agreementIdentifiers = { agreementId, policyFileId };

    const requestBodyKeyValueMap = [...requestBody.keys()]
      .filter((key) => key !== 'access_token' && key !== 'agreement_id' && key !== 'reply_uri')
      .map((key) => {
        const value = requestBody.get(key) || '';
        return <input key={key} type="hidden" name={key} value={value} />;
      });

    const onDeclineButtonClick = () => {
      if (showDeclineInlineAlerting) {
        onTermsOfUseDecline();
      } else {
        if (isInlineAlertingEnabled) {
          setShowDeclineInlineAlerting(true);
        } else {
          setIsDeclineConfirmationDialogHidden(false);
        }
      }
    };

    const onAcceptButtonClick = () => {
      if (agreement.isViewingBeforeAcceptanceRequired && !isTermsOfUseCardViewed) {
        setIsViewingRequiredDialogHidden(false);
      } else {
        onTermsOfUseAccept();
      }
    };

    const onTermsOfUseAccept = () => {
      acceptAgreementMutation.mutate(
        { agreementId: agreementId, policyFileId: policyFileId },
        {
          onSuccess: ({ readWriteConsistencyToken, digitallySignedAcceptance }) => {
            if (readWriteConsistencyTokenRef.current && readWriteConsistencyToken) {
              readWriteConsistencyTokenRef.current.value = readWriteConsistencyToken;
            }

            if (digitallySignedAcceptanceRef.current && digitallySignedAcceptance) {
              digitallySignedAcceptanceRef.current.value = digitallySignedAcceptance;
            }

            formRef.current?.submit();
          },
        },
      );
    };
    const onTermsOfUseDecline = () => {
      setIsDeclineConfirmationDialogHidden(true);
      declineAgreementMutation.mutate(
        { agreementId: agreementId, policyFileId: policyFileId },
        {
          onSuccess: () => {
            props.onTermsOfUseDecline();
          },
        },
      );
    };

    return (
      <>
        <TermsOfUseCard
          onExpand={() => setIsTermsOfUseCardViewed(true)}
          getAgreementQuery={getAgreementQuery}
          isAnonymousPage={true}
          {...agreementIdentifiers}
        />
        <div className={classNames.footnoteClass}>
          {showAcceptInlineAlerting
            ? t('acceptPopupBody')
            : t('messageTermsOfUseConsentFootnote', { acceptButtonText: t('acceptButtonText') })}
        </div>
        {showDeclineInlineAlerting && (
          <div className={classNames.inlineAlertingClass}>{declineInlineAlertingMessage}</div>
        )}
        <div>
          <DefaultButton
            className={showDeclineInlineAlerting ? classNames.alertingButtonClass : classNames.buttonClass}
            text={showDeclineInlineAlerting ? t('declineConfirmButtonText') : t('declineButtonText')}
            disabled={consentButtonsDisabled}
            onClick={onDeclineButtonClick}
            aria-label={
              showDeclineInlineAlerting
                ? t('declineConfirmButtonAriaLabel', { orgName: props.tenantDisplayName })
                : t('declineButtonText')
            }
          />
          <DeclinePopup
            tenantDisplayName={props.tenantDisplayName}
            onDismiss={() => setIsDeclineConfirmationDialogHidden(true)}
            onConfirm={onTermsOfUseDecline}
            hidden={isDeclineConfirmationDialogHidden}
          />
          <PrimaryButton
            className={classNames.buttonClass}
            text={t('acceptButtonText')}
            disabled={consentButtonsDisabled || showAcceptInlineAlerting}
            onClick={onAcceptButtonClick}
          />
          <AcceptPopup
            onDismiss={() => setIsViewingRequiredDialogHidden(true)}
            hidden={isViewingRequiredDialogHidden}
          />
          <form style={{ display: 'none' }} ref={formRef} method="POST" action={replyUri}>
            {requestBodyKeyValueMap}
            <input
              ref={readWriteConsistencyTokenRef}
              key="ReadWriteConsistencyToken"
              type="hidden"
              name="ReadWriteConsistencyToken"
              value=""
            />
            <input
              ref={digitallySignedAcceptanceRef}
              key="x-response-signature"
              type="hidden"
              name="x-response-signature"
              value=""
            />
          </form>
        </div>
      </>
    );
  }
};
